import { geoService } from '../system/geo-service';
import {
  parseCoords,
  sameStation,
  haversineKm,
  hoursDifference,
  calculateRollingWindows,
  applyFraudRules,
  getExpectedEfficiency,
  getTankCapacity
} from '../../utils/fuel-fraud-detection';
import { getConfigValue } from '../../config/fraud-detection-config';

class FuelAnalyticsService {
  // Enhanced calculateAnalytics with fraud detection
  async calculateAnalytics(transactions, driversData, shipmentsData) {
    if (!transactions || transactions.length === 0) {
      return {
        totalTransactions: 0,
        totalCost: 0,
        totalLiters: 0,
        averageCostPerLiter: 0,
        vehicleStats: {},
        stationStats: {},
        monthlyStats: {}
      };
    }

    // Step 1: Group transactions by vehicle and enrich with movement/efficiency data
    const enrichedTransactions = await this.enrichTransactions(transactions);

    // Step 2: Batch geocode unique coordinates
    await this.batchGeocodeTransactions(enrichedTransactions);

    // Step 3: Calculate analytics with fraud detection
    const analytics = this.calculateEnhancedAnalytics(enrichedTransactions, driversData, shipmentsData);

    return {
      ...analytics,
      enrichedTransactions
    };
  }

  // Enrich transactions with movement, efficiency, and fraud detection
  async enrichTransactions(transactions) {
    // Group by vehicle and sort by fill_time
    const vehicleGroups = this.groupTransactionsByVehicle(transactions);
    const enrichedTransactions = [];

    for (const [vehiclePlate, vehicleTransactions] of vehicleGroups) {
      // Sort by fill_time
      const sortedTransactions = vehicleTransactions.sort((a, b) => {
        const aTime = a.fill_time?.toDate ? a.fill_time.toDate() : new Date(a.fill_time);
        const bTime = b.fill_time?.toDate ? b.fill_time.toDate() : new Date(b.fill_time);
        return aTime - bTime;
      });

      // Process each transaction in chronological order
      let estFuelL = null; // Start with null - we don't know initial fuel level

      for (let i = 0; i < sortedTransactions.length; i++) {
        const transaction = sortedTransactions[i];
        const prevTransaction = i > 0 ? sortedTransactions[i - 1] : null;

        // Enrich with movement and efficiency data
        const enriched = this.enrichSingleTransaction(transaction, prevTransaction);

        // Update estimated fuel level - only if we have distance and efficiency data
        if (prevTransaction && enriched.distanceBasisKm && enriched.distanceBasisKm > 0) {
          const expectedEff = getExpectedEfficiency();
          const tankCapacity = getTankCapacity();
          
          if (expectedEff > 0 && tankCapacity > 0) {
            const fuelUsed = enriched.distanceBasisKm / expectedEff;
            
            // Initialize fuel level if this is our first calculation
            if (estFuelL === null) {
              estFuelL = tankCapacity * getConfigValue('defaults.INITIAL_FUEL_RATIO', 0.5);
            }
            
            estFuelL = Math.max(0, Math.min(tankCapacity, estFuelL - fuelUsed + enriched.liters));
          }
        } else if (estFuelL !== null) {
          // If we have a fuel estimate but no distance, just add the fuel
          estFuelL = Math.min(getTankCapacity(), estFuelL + enriched.liters);
        }
        
        enriched.estFuelL = estFuelL;

        // Calculate rolling windows for fraud detection
        const rollingWindows = calculateRollingWindows(sortedTransactions, i);

        // Apply fraud rules
        const fraudResult = applyFraudRules(enriched, prevTransaction, rollingWindows);
        enriched.fraudFlags = fraudResult.flags;
        enriched.fraudScore = fraudResult.score;

        // Add rolling window data for display
        enriched.fillsIn24h = rollingWindows.fillsIn24h;
        enriched.smallCount24h = rollingWindows.smallCount24h;
        enriched.smallSum24h = rollingWindows.smallSum24h;
        enriched.smallSameStation12h = rollingWindows.smallSameStation12h;

        enrichedTransactions.push(enriched);
      }
    }

    return enrichedTransactions;
  }

  // Group transactions by vehicle
  groupTransactionsByVehicle(transactions) {
    const groups = new Map();

    for (const transaction of transactions) {
      const vehiclePlate = transaction.mappedVehiclePlate || transaction.vehicle_plate || 'Unknown Vehicle';
      
      if (!groups.has(vehiclePlate)) {
        groups.set(vehiclePlate, []);
      }
      
      groups.get(vehiclePlate).push(transaction);
    }

    return groups;
  }

  // Enrich a single transaction with movement and efficiency data
  enrichSingleTransaction(transaction, prevTransaction) {
    const enriched = { ...transaction };

    // Parse coordinates
    enriched.coordsParsed = parseCoords(transaction.station_coordinates);
    enriched.googleMapsUrl = geoService.generateGoogleMapsUrl(transaction.station_coordinates);

    // Calculate movement metrics
    if (prevTransaction) {
      const prevCoords = parseCoords(prevTransaction.station_coordinates);
      
      if (enriched.coordsParsed && prevCoords) {
        enriched.kmFromLast = haversineKm(prevCoords, enriched.coordsParsed);
      } else {
        enriched.kmFromLast = null;
      }

      enriched.hrsFromLast = hoursDifference(prevTransaction.fill_time, transaction.fill_time);
    } else {
      enriched.kmFromLast = null;
      enriched.hrsFromLast = null;
    }

    // Calculate odometer delta - only if both readings exist and are valid
    const hasOdoPair = prevTransaction?.odometer != null && transaction.odometer != null;
    if (hasOdoPair) {
      const currentOdo = parseFloat(transaction.odometer);
      const prevOdo = parseFloat(prevTransaction.odometer);
      
      if (currentOdo > prevOdo) {
        enriched.odoDelta = currentOdo - prevOdo;
      } else {
        enriched.odoDelta = null; // Negative or zero delta
      }
    } else {
      enriched.odoDelta = null;
    }

    // Determine distance basis (prefer odometer, fallback to GPS) - NO coercion to 0
    enriched.distanceBasisKm = (enriched.odoDelta && enriched.odoDelta > 0) 
      ? enriched.odoDelta 
      : (enriched.kmFromLast && enriched.kmFromLast > 0) 
        ? enriched.kmFromLast 
        : null;

    // Calculate efficiency - only if we have valid distance and liters
    const liters = parseFloat(transaction.liters) || 0;
    if (enriched.distanceBasisKm && enriched.distanceBasisKm > 0 && liters > 0) {
      enriched.efficiencyKmPerL = enriched.distanceBasisKm / liters;
    } else {
      enriched.efficiencyKmPerL = null;
    }

    return enriched;
  }

  // Batch geocode unique coordinates
  async batchGeocodeTransactions(transactions) {
    // Collect unique coordinates
    const uniqueCoords = new Set();
    for (const transaction of transactions) {
      if (transaction.coordsParsed) {
        const key = geoService.getCacheKey(transaction.coordsParsed);
        uniqueCoords.add(transaction.coordsParsed);
      }
    }

    // Batch geocode
    const geocodeResults = await geoService.batchReverseGeocode(Array.from(uniqueCoords));

    // Attach results to transactions
    for (const transaction of transactions) {
      if (transaction.coordsParsed) {
        const key = geoService.getCacheKey(transaction.coordsParsed);
        const result = geocodeResults.get(key);
        if (result) {
          transaction.city = result.city;
          transaction.district = result.district;
        }
      }
    }
  }

  // Calculate enhanced analytics with per-vehicle KPIs
  calculateEnhancedAnalytics(enrichedTransactions, driversData, shipmentsData) {
    const vehicleStats = {};
    const stationStats = {};
    const monthlyStats = {};

    let totalCost = 0;
    let totalLiters = 0;

    // Create a map of vehicle plates to drivers from shipments
    const vehicleToDriverMap = {};
    shipmentsData.forEach(shipment => {
      if (shipment.vehiclePlate && shipment.driverName) {
        vehicleToDriverMap[shipment.vehiclePlate] = shipment.driverName;
      }
    });

    enrichedTransactions.forEach(transaction => {
      const cost = parseFloat(transaction.cost) || 0;
      const liters = parseFloat(transaction.liters) || 0;

      totalCost += cost;
      totalLiters += liters;

      // Vehicle statistics with enhanced KPIs
      const vehiclePlate = transaction.mappedVehiclePlate || transaction.vehicle_plate || 'Unknown Vehicle';
      const associatedDriver = vehicleToDriverMap[vehiclePlate] || 'غير محدد';

      if (!vehicleStats[vehiclePlate]) {
        vehicleStats[vehiclePlate] = {
          transactions: 0,
          totalCost: 0,
          totalLiters: 0,
          vehiclePlate: vehiclePlate,
          associatedDriver: associatedDriver,
          kmDriven: 0,
          costPerKm: 0,
          lPer100km: 0,
          suspiciousTransactions: 0,
          totalFraudScore: 0
        };
      }

      const vStats = vehicleStats[vehiclePlate];
      vStats.transactions += 1;
      vStats.totalCost += cost;
      vStats.totalLiters += liters;

      // Add distance driven (only positive odometer deltas)
      if (transaction.odoDelta && transaction.odoDelta > 0) {
        vStats.kmDriven += transaction.odoDelta;
      }

      // Track suspicious transactions (high-severity only for counts)
      if (transaction.fraudScore >= 5) {
        vStats.suspiciousTransactions += 1;
      }
      if (transaction.fraudScore > 0) {
        vStats.totalFraudScore += transaction.fraudScore;
      }

      // Station statistics with location info
      const stationKey = transaction.station_name || 
        (transaction.coordsParsed ? `${transaction.coordsParsed.lat.toFixed(3)},${transaction.coordsParsed.lng.toFixed(3)}` : 'Unknown Station');
      
      if (!stationStats[stationKey]) {
        stationStats[stationKey] = {
          transactions: 0,
          totalCost: 0,
          totalLiters: 0,
          coordinates: transaction.station_coordinates,
          city: transaction.city,
          district: transaction.district,
          suspiciousTransactions: 0
        };
      }

      const sStats = stationStats[stationKey];
      sStats.transactions += 1;
      sStats.totalCost += cost;
      sStats.totalLiters += liters;

      if (transaction.fraudScore >= 5) {
        sStats.suspiciousTransactions += 1;
      }

      // Monthly statistics
      if (transaction.fill_time) {
        const date = transaction.fill_time.toDate ? transaction.fill_time.toDate() : new Date(transaction.fill_time);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyStats[monthKey]) {
          monthlyStats[monthKey] = {
            transactions: 0,
            totalCost: 0,
            totalLiters: 0,
            suspiciousTransactions: 0
          };
        }
        monthlyStats[monthKey].transactions += 1;
        monthlyStats[monthKey].totalCost += cost;
        monthlyStats[monthKey].totalLiters += liters;

        if (transaction.fraudScore >= 5) {
          monthlyStats[monthKey].suspiciousTransactions += 1;
        }
      }
    });

    // Calculate per-vehicle KPIs
    Object.values(vehicleStats).forEach(vStats => {
      if (vStats.kmDriven > 0) {
        vStats.costPerKm = vStats.totalCost / vStats.kmDriven;
        vStats.lPer100km = (vStats.totalLiters / vStats.kmDriven) * 100;
      }
    });

    // Get top 5 vehicles by cost per km
    const topCostPerKm = Object.values(vehicleStats)
      .filter(v => v.costPerKm > 0)
      .sort((a, b) => b.costPerKm - a.costPerKm)
      .slice(0, 5);

    return {
      totalTransactions: enrichedTransactions.length,
      totalCost,
      totalLiters,
      averageCostPerLiter: totalLiters > 0 ? totalCost / totalLiters : 0,
      vehicleStats,
      stationStats,
      monthlyStats,
      topCostPerKm,
      suspiciousTransactionsCount: enrichedTransactions.filter(t => t.fraudScore >= 5).length // Only high-severity
    };
  }
}

export const fuelAnalyticsService = new FuelAnalyticsService();